<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportHeader from './components/ReportHeader.vue';
import crmMetricsAPI from 'dashboard/api/crmMetrics';

const { t } = useI18n();

const PERIODS = computed(() => [
  { key: 'daily', label: t('CRM_METRICS.PERIOD.DAILY') },
  { key: 'weekly', label: t('CRM_METRICS.PERIOD.WEEKLY') },
  { key: 'monthly', label: t('CRM_METRICS.PERIOD.MONTHLY') },
  { key: 'semester', label: t('CRM_METRICS.PERIOD.SEMESTER') },
  { key: 'annual', label: t('CRM_METRICS.PERIOD.ANNUAL') },
  { key: 'total', label: t('CRM_METRICS.PERIOD.TOTAL') },
]);

const PREVIOUS_LABELS = {
  daily: 'CRM_METRICS.PREVIOUS_LABELS.DAILY',
  weekly: 'CRM_METRICS.PREVIOUS_LABELS.WEEKLY',
  monthly: 'CRM_METRICS.PREVIOUS_LABELS.MONTHLY',
  semester: 'CRM_METRICS.PREVIOUS_LABELS.SEMESTER',
  annual: 'CRM_METRICS.PREVIOUS_LABELS.ANNUAL',
};

const period = ref('monthly');
const isLoading = ref(false);
const data = ref(null);

const current = computed(() => data.value?.current || null);
const previous = computed(() => data.value?.previous || null);
const hasPrevious = computed(() => !!previous.value);

const previousPeriodLabel = computed(() =>
  PREVIOUS_LABELS[period.value] ? t(PREVIOUS_LABELS[period.value]) : ''
);

async function fetchMetrics() {
  isLoading.value = true;
  try {
    const response = await crmMetricsAPI.get(period.value);
    data.value = response.data;
  } catch {
    data.value = null;
  } finally {
    isLoading.value = false;
  }
}

function setPeriod(p) {
  period.value = p;
  fetchMetrics();
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);
}

// Group metric_cards by funnel, preserving funnel order
function funnelGroups(metrics) {
  const cards = (metrics?.metric_cards || []).filter(c => !c.currency_only);
  const map = new Map();
  cards.forEach(card => {
    if (!map.has(card.funnel_key)) {
      map.set(card.funnel_key, {
        funnel_key: card.funnel_key,
        funnel_name: card.funnel_name,
        stages: [],
      });
    }
    map.get(card.funnel_key).stages.push(card);
  });
  return Array.from(map.values());
}

function standaloneValueCards(metrics) {
  return (metrics?.metric_cards || []).filter(c => c.currency_only);
}

function overdueItems(metrics) {
  return (metrics?.overdue_dates || []).filter(d => d.count > 0);
}

function funnelTotal(stages) {
  return stages.reduce((sum, s) => sum + (s.count || 0), 0);
}

onMounted(fetchMetrics);
</script>

<template>
  <div>
    <ReportHeader :header-title="$t('CRM_METRICS.TITLE')" />

    <!-- Filtro de período -->
    <div class="flex flex-wrap items-center gap-2 mb-8">
      <button
        v-for="p in PERIODS"
        :key="p.key"
        class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        :class="
          period === p.key
            ? 'bg-woot-500 text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="setPeriod(p.key)"
      >
        {{ p.label }}
      </button>
      <button
        class="ml-auto px-3 py-2 rounded-lg text-sm font-medium bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 transition-colors flex items-center gap-1.5"
        :class="{ 'opacity-50 pointer-events-none': isLoading }"
        @click="fetchMetrics"
      >
        <span
          class="i-lucide-refresh-cw text-base"
          :class="{ 'animate-spin': isLoading }"
        />
        {{ $t('CRM_METRICS.REFRESH') }}
      </button>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <span class="text-n-slate-11 text-sm">{{
        $t('CRM_METRICS.LOADING')
      }}</span>
    </div>

    <template v-else-if="current">
      <!-- Canal dos Leads (global) -->
      <template v-if="current.canal_leads?.length">
        <h2
          class="text-sm font-semibold text-n-slate-11 uppercase tracking-wide mb-3"
        >
          {{ $t('CRM_METRICS.CANAL_LEADS') }}
        </h2>
        <div class="grid grid-cols-3 gap-3 mb-8">
          <div
            v-for="item in current.canal_leads"
            :key="item.canal"
            class="bg-n-solid-1 border border-n-weak rounded-xl p-4 flex items-center justify-between"
          >
            <span class="text-sm text-n-slate-11">{{ item.canal }}</span>
            <span class="text-lg font-semibold text-n-slate-12">{{
              item.count
            }}</span>
          </div>
        </div>
      </template>

      <!-- Seções por funil -->
      <div
        v-for="group in funnelGroups(current)"
        :key="group.funnel_key"
        class="mb-8"
      >
        <!-- Cabeçalho do funil -->
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-bold text-n-slate-12">
            {{ group.funnel_name }}
          </h2>
          <span
            class="text-sm text-n-slate-11 bg-n-alpha-2 px-3 py-1 rounded-full"
          >
            {{ $t('CRM_METRICS.TOTAL_LEADS') }}:
            <strong>{{ funnelTotal(group.stages) }}</strong>
          </span>
        </div>

        <!-- Grid de estágios -->
        <div class="grid grid-cols-2 gap-4">
          <div
            v-for="stage in group.stages"
            :key="stage.label"
            class="bg-n-solid-1 border border-n-weak rounded-xl p-5"
          >
            <p class="text-sm font-semibold text-n-slate-12 mb-3">
              {{ stage.label }}
            </p>
            <div class="flex items-end justify-between gap-2">
              <div>
                <p class="text-xs text-n-slate-11">
                  {{ $t('CRM_METRICS.QUANTITY') }}
                </p>
                <p class="text-2xl font-bold text-n-slate-12">
                  {{ stage.count }}
                </p>
              </div>
              <div v-if="stage.value !== undefined" class="text-right">
                <p class="text-xs text-n-slate-11">
                  {{ stage.currency_label }}
                </p>
                <p class="text-lg font-semibold text-woot-500">
                  {{ formatCurrency(stage.value) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Valores sem funil associado -->
      <template v-if="standaloneValueCards(current).length">
        <div class="grid grid-cols-2 gap-4 mb-8">
          <div
            v-for="card in standaloneValueCards(current)"
            :key="card.label"
            class="bg-n-solid-1 border border-n-weak rounded-xl p-5"
          >
            <p class="text-sm font-medium text-n-slate-11 mb-2">
              {{ card.label }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ formatCurrency(card.value) }}
            </p>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ $t('CRM_METRICS.CONTACT_COUNT', card.count) }}
            </p>
          </div>
        </div>
      </template>

      <!-- Manutenções Atrasadas (global) -->
      <template v-if="overdueItems(current).length">
        <h2
          class="text-sm font-semibold text-n-slate-11 uppercase tracking-wide mb-3"
        >
          {{ $t('CRM_METRICS.OVERDUE_TITLE') }}
        </h2>
        <div class="grid grid-cols-2 gap-4 mb-8">
          <div
            v-for="item in overdueItems(current)"
            :key="item.attribute_key"
            class="bg-n-solid-1 border border-ruby-500 rounded-xl p-5"
          >
            <p class="text-sm font-medium text-n-slate-11 mb-2">
              {{ item.attribute_display_name }}
            </p>
            <p class="text-3xl font-bold text-ruby-500">{{ item.count }}</p>
            <p class="text-xs text-n-slate-11 mt-1">
              {{
                $t('CRM_METRICS.OVERDUE_MONTHS', {
                  months: item.threshold_months,
                })
              }}
            </p>
          </div>
        </div>
      </template>

      <!-- Período Anterior -->
      <template v-if="hasPrevious">
        <div class="border-t border-n-weak my-6" />
        <h2
          class="text-sm font-semibold text-n-slate-11 uppercase tracking-wide mb-6"
        >
          {{ previousPeriodLabel }}
        </h2>

        <div
          v-for="group in funnelGroups(previous)"
          :key="group.funnel_key"
          class="mb-8"
        >
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-base font-bold text-n-slate-12">
              {{ group.funnel_name }}
            </h2>
            <span
              class="text-sm text-n-slate-11 bg-n-alpha-2 px-3 py-1 rounded-full"
            >
              {{ $t('CRM_METRICS.TOTAL_LEADS') }}:
              <strong>{{ funnelTotal(group.stages) }}</strong>
            </span>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div
              v-for="stage in group.stages"
              :key="stage.label"
              class="bg-n-solid-1 border border-n-weak rounded-xl p-5"
            >
              <p class="text-sm font-semibold text-n-slate-12 mb-3">
                {{ stage.label }}
              </p>
              <div class="flex items-end justify-between gap-2">
                <div>
                  <p class="text-xs text-n-slate-11">
                    {{ $t('CRM_METRICS.QUANTITY') }}
                  </p>
                  <p class="text-2xl font-bold text-n-slate-12">
                    {{ stage.count }}
                  </p>
                </div>
                <div v-if="stage.value !== undefined" class="text-right">
                  <p class="text-xs text-n-slate-11">
                    {{ stage.currency_label }}
                  </p>
                  <p class="text-lg font-semibold text-n-slate-10">
                    {{ formatCurrency(stage.value) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-if="standaloneValueCards(previous).length">
          <div class="grid grid-cols-2 gap-4 mb-6">
            <div
              v-for="card in standaloneValueCards(previous)"
              :key="card.label"
              class="bg-n-solid-1 border border-n-weak rounded-xl p-5"
            >
              <p class="text-sm font-medium text-n-slate-11 mb-2">
                {{ card.label }}
              </p>
              <p class="text-2xl font-bold text-n-slate-12">
                {{ formatCurrency(card.value) }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ $t('CRM_METRICS.CONTACT_COUNT', card.count) }}
              </p>
            </div>
          </div>
        </template>
      </template>
    </template>

    <div v-else-if="!isLoading" class="flex items-center justify-center py-20">
      <span class="text-n-slate-11 text-sm">{{
        $t('CRM_METRICS.NO_DATA')
      }}</span>
    </div>
  </div>
</template>
